var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from "react";
import { Alert, Box, CircularProgress, FormControlLabel, Stack, Switch } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { MONITORING_SET_MODE } from "@/queries/MonitoringSetMode";
import { IPermissions } from "@/types/keycloak";
import { MONITORING_LIST_DASHBOARDS } from "@/queries/MonitoringListDashboards";
import { EMonitoringCapability, EMonitoringMode, } from "./types";
import { EMonitoringLock, useMonitoringPermissions } from "./useMonitoringPermissions";
import { MonitoringProvider } from "./MonitoringProvider";
import { MonitoringDashboard } from "./MonitoringDashboard";
import { useMonitoringTabEditor } from "./editor/useMonitoringTabEditor";
import { StandardDashboardContent } from "../dashboard/DashboardContent";
/**
 * The Dashboard tab. Monitoring replaces the standard dashboard only for a
 * viewer with `monitoring-view` on an event whose monitoring is configured;
 * anyone else keeps the standard dashboard, and no monitoring request is made
 * for a viewer without the permission.
 */
export function MonitoringDashboardTab(_a) {
    var _this = this;
    var _b;
    var electionEventId = _a.electionEventId, electionId = _a.electionId, legacy = _a.legacy, below = _a.below, footer = _a.footer, _c = _a.lock, lock = _c === void 0 ? EMonitoringLock.OPEN : _c, actions = _a.actions, editorApi = _a.editorApi;
    var t = useTranslation().t;
    var _d = useMonitoringPermissions(lock), view = _d.view, configure = _d.configure;
    var _e = useState(false), modeError = _e[0], setModeError = _e[1];
    var _f = useMutation(MONITORING_SET_MODE, {
        context: { headers: { "x-hasura-role": IPermissions.MONITORING_CONFIGURE } },
        refetchQueries: [MONITORING_LIST_DASHBOARDS],
        awaitRefetchQueries: true,
    }), setMode = _f[0], changingMode = _f[1].loading;
    var changeMode = function (enabled) { return __awaiter(_this, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!electionEventId || configure !== EMonitoringCapability.GRANTED || changingMode)
                        return [2 /*return*/];
                    setModeError(false);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, setMode({
                            variables: {
                                electionEventId: electionEventId,
                                mode: enabled ? EMonitoringMode.CONFIGURED : EMonitoringMode.LEGACY,
                            },
                        })];
                case 2:
                    _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    setModeError(true);
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var editor = useMonitoringTabEditor({
        electionEventId: electionEventId !== null && electionEventId !== void 0 ? electionEventId : "",
        electionId: electionId,
        configure: configure,
        api: editorApi,
    });
    var allowed = view === EMonitoringCapability.GRANTED && Boolean(electionEventId);
    var _g = useQuery(MONITORING_LIST_DASHBOARDS, {
        variables: { electionEventId: electionEventId !== null && electionEventId !== void 0 ? electionEventId : "", electionId: electionId !== null && electionId !== void 0 ? electionId : null },
        skip: !allowed,
    }), data = _g.data, loading = _g.loading, error = _g.error;
    var standard = (_jsxs(StandardDashboardContent, { children: [legacy, footer] }));
    if (!allowed)
        return standard;
    if (error) {
        return (_jsxs(Stack, { spacing: 2, children: [_jsx(Alert, { severity: "warning", children: t("monitoring.unavailableAlert") }), standard] }));
    }
    var list = data === null || data === void 0 ? void 0 : data.monitoringListDashboards;
    if (loading || !list) {
        return (_jsxs(_Fragment, { children: [_jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 4 }, children: _jsx(CircularProgress, { "aria-label": t("monitoring.loading") }) }), footer] }));
    }
    var controls = (_jsxs(Stack, { spacing: 1, sx: { mb: 2 }, children: [_jsx(FormControlLabel, { label: t("monitoring.configuredDashboards"), control: _jsx(Switch, { inputProps: { role: "switch" }, checked: list.mode === EMonitoringMode.CONFIGURED, disabled: configure !== EMonitoringCapability.GRANTED || changingMode, onChange: function (_event, checked) { return void changeMode(checked); } }) }), modeError && _jsx(Alert, { severity: "error", children: t("monitoring.modeChangeError") })] }));
    if (list.mode !== EMonitoringMode.CONFIGURED)
        return (_jsxs(_Fragment, { children: [controls, standard] }));
    if (!list.dashboards.length) {
        return (_jsxs(Stack, { spacing: 2, children: [controls, _jsx(Alert, { severity: "info", children: t("monitoring.noDashboards") }), below, footer] }));
    }
    return (_jsxs(_Fragment, { children: [controls, _jsxs(MonitoringProvider, { storageKey: "monitoring:".concat(electionEventId, ":").concat(electionId !== null && electionId !== void 0 ? electionId : ""), actions: actions !== null && actions !== void 0 ? actions : editor.actions, children: [_jsx(MonitoringDashboard, { electionEventId: electionEventId, electionId: electionId, dashboards: list.dashboards, presetTitle: (_b = list.preset) === null || _b === void 0 ? void 0 : _b.title, configure: configure, refreshSeconds: list.refresh_seconds }), editor.element] }), below, footer] }));
}
